import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { ui } from "../lib/ui";

interface Producto {
  id: number;
  sku: string;
  nombre: string;
  categoria?: string | null;
  precio_venta: number;
  activo: boolean;
}

type Modo = "porcentaje" | "fijo";
type Target = "categoria" | "skus";

export default function Precios() {
  const qc = useQueryClient();
  const [target, setTarget] = useState<Target>("categoria");
  const [categoria, setCategoria] = useState("");
  const [skusText, setSkusText] = useState("");
  const [modo, setModo] = useState<Modo>("porcentaje");
  const [valor, setValor] = useState<number>(0);
  const [motivo, setMotivo] = useState("");
  const [confirming, setConfirming] = useState(false);

  const { data: productos } = useQuery({
    queryKey: ["productos", "all-for-precios"],
    queryFn: async () => {
      const { data } = await api.get<Producto[]>("/api/products", {
        params: { limit: 500 },
      });
      return data;
    },
  });

  const categorias = useMemo(() => {
    const set = new Set<string>();
    productos?.forEach((p) => p.categoria && set.add(p.categoria));
    return Array.from(set).sort();
  }, [productos]);

  const skusList = useMemo(
    () =>
      skusText
        .split(/[\s,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [skusText]
  );

  const afectados = useMemo(() => {
    if (!productos) return [];
    let list = productos.filter((p) => p.activo);
    if (target === "categoria" && categoria) {
      list = list.filter((p) => p.categoria === categoria);
    } else if (target === "skus" && skusList.length) {
      const set = new Set(skusList);
      list = list.filter((p) => set.has(p.sku));
    } else {
      return [];
    }
    return list.map((p) => {
      const anterior = Number(p.precio_venta);
      let nuevo = anterior;
      if (modo === "fijo") {
        nuevo = valor;
      } else if (modo === "porcentaje") {
        nuevo = Math.round(anterior * (1 + valor / 100) * 100) / 100;
      }
      return { ...p, anterior, nuevo };
    });
  }, [productos, target, categoria, skusList, modo, valor]);

  const aplicar = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { motivo: motivo || undefined };
      if (target === "categoria") payload.categoria = categoria;
      else payload.skus = skusList;
      if (modo === "porcentaje") payload.porcentaje = valor;
      else payload.nuevo_precio_fijo = valor;
      await api.post("/api/prices/bulk", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["productos"] });
      setConfirming(false);
      setValor(0);
      setMotivo("");
    },
  });

  return (
    <div className="flex-1 flex flex-col h-full">
      <header className={ui.header}>
        <div>
          <h1 className={ui.title}>Precios</h1>
          <p className={ui.subtitle}>Cambios masivos con preview</p>
        </div>
      </header>

      <div className={ui.page}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className={ui.cardPad}>
            <h2 className="text-lg font-bold text-slate-900 mb-4">
              Configurar cambio
            </h2>
            <div className="space-y-3">
              <div>
                <label className={ui.label}>Alcance</label>
                <div className="flex gap-2">
                  <button
                    className={
                      target === "categoria"
                        ? "flex-1 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-medium"
                        : "flex-1 bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium"
                    }
                    onClick={() => setTarget("categoria")}
                  >
                    Por categoría
                  </button>
                  <button
                    className={
                      target === "skus"
                        ? "flex-1 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-medium"
                        : "flex-1 bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium"
                    }
                    onClick={() => setTarget("skus")}
                  >
                    Lista de SKUs
                  </button>
                </div>
              </div>

              {target === "categoria" ? (
                <div>
                  <label className={ui.label}>Categoría</label>
                  <select
                    className={ui.input}
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value)}
                  >
                    <option value="">— Elegir —</option>
                    {categorias.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className={ui.label}>SKUs (uno por línea o separados)</label>
                  <textarea
                    className={ui.input}
                    rows={4}
                    placeholder="SKU001&#10;SKU002&#10;SKU003"
                    value={skusText}
                    onChange={(e) => setSkusText(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label className={ui.label}>Tipo de cambio</label>
                <div className="flex gap-2">
                  <button
                    className={
                      modo === "porcentaje"
                        ? "flex-1 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-medium"
                        : "flex-1 bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium"
                    }
                    onClick={() => setModo("porcentaje")}
                  >
                    Porcentaje
                  </button>
                  <button
                    className={
                      modo === "fijo"
                        ? "flex-1 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-medium"
                        : "flex-1 bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-medium"
                    }
                    onClick={() => setModo("fijo")}
                  >
                    Precio fijo
                  </button>
                </div>
              </div>

              <div>
                <label className={ui.label}>
                  {modo === "porcentaje" ? "Porcentaje (%)" : "Nuevo precio"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  className={ui.input}
                  value={valor}
                  onChange={(e) => setValor(parseFloat(e.target.value) || 0)}
                />
              </div>

              <div>
                <label className={ui.label}>Motivo</label>
                <input
                  className={ui.input}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="ej: lista mayo 2026"
                />
              </div>
            </div>
          </div>

          <div className={ui.cardPad}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">
                Preview ({afectados.length} productos)
              </h2>
              <button
                className={ui.btnPrimary}
                disabled={!afectados.length || aplicar.isPending}
                onClick={() => setConfirming(true)}
              >
                Aplicar
              </button>
            </div>

            <div className="max-h-[500px] overflow-y-auto">
              {!afectados.length ? (
                <p className={ui.empty}>
                  Configurá alcance y valor para ver el preview.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 uppercase tracking-wide">
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2">SKU</th>
                      <th className="text-left">Producto</th>
                      <th className="text-right">Antes</th>
                      <th className="text-right">Después</th>
                    </tr>
                  </thead>
                  <tbody>
                    {afectados.map((p) => (
                      <tr key={p.id} className="border-b border-slate-100">
                        <td className="py-2 font-mono text-xs">{p.sku}</td>
                        <td>{p.nombre}</td>
                        <td className="text-right text-slate-500 line-through">
                          {money(p.anterior)}
                        </td>
                        <td className="text-right font-bold text-emerald-600">
                          {money(p.nuevo)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {confirming && (
        <div className={ui.modalOverlay} onClick={() => setConfirming(false)}>
          <div className={ui.modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              Confirmar cambio
            </h2>
            <p className="text-sm text-slate-600 mb-4">
              Vas a modificar el precio de{" "}
              <span className="font-bold">{afectados.length}</span> productos.
              Esta acción no se puede deshacer fácilmente.
            </p>
            {aplicar.isError && (
              <div className={ui.error + " mb-3"}>
                Error aplicando cambios.
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                className={ui.btnSecondary}
                onClick={() => setConfirming(false)}
              >
                Cancelar
              </button>
              <button
                className={ui.btnPrimary}
                disabled={aplicar.isPending}
                onClick={() => aplicar.mutate()}
              >
                {aplicar.isPending ? "Aplicando..." : "Confirmar y aplicar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
